import dayjs from 'dayjs';
import type { PhaseLabel } from '../../helpers/phase-labels/phase-labels.type';
import { Measure } from './work-periods-client.type';
import type { MeasureDataResponse, MeasureMetadata } from './work-periods-client.type';

/**
 * Measures that are stored as custom (DSL) calculations even though they have a
 * dedicated Measure enum value, so they must route through the custom endpoints
 * (e.g. /custom-measure/, /custom-tasks/) regardless of the metadata flag.
 */
const CUSTOM_ONE_OFF_MEASURES: Measure[] = [
  Measure.Readiness,
  Measure.Complexity,
  Measure.InitialCommittedPoints,
  Measure.InitialCommittedTasks,
];

/**
 * Every built-in measure has a `Measure` enum value; user-defined custom (DSL)
 * measures are referenced by their CalculationConfig name, which is never an enum
 * value. So a measure name absent from this set is necessarily custom.
 */
const BUILT_IN_MEASURE_NAMES = new Set<string>(Object.values(Measure));

/**
 * Determine whether a measure should be treated as a custom (DSL) calculation so
 * the client routes it to the custom endpoints.
 *
 * A measure is custom when:
 *  - it is one of the built-in-but-DSL one-offs (Readiness, Complexity), or
 *  - the available-measures metadata flags it `is_custom`, or
 *  - its name is not a built-in `Measure` enum value (i.e. a CalculationConfig name).
 *
 * The last clause makes this resolve correctly even before `availableMeasures` has
 * loaded, so a custom measure is never misrouted to the built-in endpoint on the
 * first request (the metadata is only needed for the rare case of a custom config
 * whose name collides with a built-in enum value).
 *
 * @param {Measure} measureName - The measure to check
 * @param {MeasureMetadata[]} availableMeasures - Measure metadata for the current scope (optional)
 * @returns {boolean} True when the measure must route to the custom endpoints
 */
const isCustomMeasure = (measureName: Measure, availableMeasures: MeasureMetadata[] = []): boolean =>
  CUSTOM_ONE_OFF_MEASURES.includes(measureName) ||
  availableMeasures.some((measure) => measure.measure_name === measureName && measure.is_custom === true) ||
  !BUILT_IN_MEASURE_NAMES.has(measureName);

/**
 * Generates a mock object with random values for each measure within a given date range.
 *
 * @param {Measure[]} measures - An array of measures to generate mock values for.
 * @param {string} startDate - The start date of the date range.
 * @param {string} endDate - The end date of the date range.
 * @return {Record<Measure, Record<string, number>>} - A mock object with measure values for each date within the range.
 */
const generateMock = (measures: Measure[], startDate: string, endDate: string): MeasureDataResponse => {
  const mock = measures.reduce((acc, measure) => {
    const measureValue = generateRange(startDate, endDate).reduce((acc, date) => {
      return {
        ...acc,
        [date]: randomize(),
      };
    }, {});

    return {
      ...acc,
      [measure]: measureValue,
    };
  }, {} as MeasureDataResponse);

  return mock;
};

/**
 * Generates an array of dates between the start and end dates.
 *
 * @param {string} startDate - The start date in the format 'YYYY-MM-DD'.
 * @param {string} endDate - The end date in the format 'YYYY-MM-DD'.
 * @return {string[]} An array of dates between the start and end dates.
 */
const generateRange = (startDate: string, endDate: string): string[] => {
  const list = [];
  let current = dayjs(startDate);
  while (current.isBefore(endDate)) {
    list.push(current.format('YYYY-MM-DD'));
    current = current.add(1, 'day');
  }
  return list;
};

/**
 * Generates a random number between 1 and 100 (inclusive).
 *
 * @return {number} The randomly generated number.
 */
const randomize = (): number => {
  return Math.random() * (100 - 1) + 1;
};

/**
 * Sanitizes measure data response by handling undefined values and creating
 * default empty objects, while preserving the optional top-level `phases`
 * companion array forwarded by phase-aware backend responses.
 *
 * Responsibilities:
 * 1. When `data` is present, iterate every `[key, value]` pair and replace
 *    null/undefined per-measure values with empty objects so downstream
 *    consumers can index without optional chaining.
 * 2. Forward a top-level `phases` array when it is an array, narrowed to
 *    well-formed `{ key: string; label: string }` entries so malformed
 *    items (null, primitives, missing fields) cannot leak downstream.
 *    Drop non-array values for the same key (defensive against malformed
 *    responses).
 * 3. When `data` is undefined, return an object with one empty entry per
 *    requested measure name so the consumer's reducer-style code paths can
 *    still iterate without a null guard.
 *
 * @param {MeasureDataResponse | undefined} data - The measure data response
 *   to sanitize. May contain a `phases` array alongside per-measure entries.
 * @param {string[]} measureNames - Measure names used to build the empty
 *   default response when `data` is missing.
 * @returns {MeasureDataResponse} Sanitized response. Per-measure entries
 *   never contain null/undefined values; `phases` is preserved when it was
 *   a valid array on the input.
 */
const sanitizeMeasureDataResponse = (
  data: MeasureDataResponse | undefined,
  measureNames: string[],
): MeasureDataResponse => {
  if (data) {
    const sanitized = {} as MeasureDataResponse;

    for (const [key, value] of Object.entries(data) as [string, unknown][]) {
      if (key === 'phases') {
        if (Array.isArray(value)) {
          sanitized.phases = value.filter(
            (item): item is PhaseLabel =>
              item != null &&
              typeof item === 'object' &&
              typeof (item as { key?: unknown }).key === 'string' &&
              typeof (item as { label?: unknown }).label === 'string',
          );
        }
        continue;
      }
      sanitized[key as Measure] =
        value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, number>) : {};
    }

    return sanitized;
  }

  const defaultMeasureResponse = {} as MeasureDataResponse;

  for (const measure of measureNames) {
    defaultMeasureResponse[measure as Measure] = {};
  }

  return defaultMeasureResponse;
};

export { generateMock, isCustomMeasure, sanitizeMeasureDataResponse };
